import { TITLE_PREFIX_RE } from '@/features/stage-marker/stage';
import { composeFavicon } from './favicon';
import type {
  FaviconPaint,
  FaviconRenderer,
  HeadCue,
  HeadCueLayer,
  SetLayerOptions,
} from './types';

/**
 * The single owner of the tab's title prefixes and icon hrefs.
 *
 * - Title: every layer's `titlePrefix`, outer to inner by `order`, then the
 *   page's own title (`rest`). Written synchronously on every change, and
 *   re-applied by one head observer when Next rewrites or replaces <title>.
 * - Icon: one render per tick (renders coalesce in a microtask), fed the
 *   layers' paints in order with every `faviconLast` paint at the end. It waits
 *   for every layer's `faviconGate`, and a render that finishes after a newer
 *   change is dropped (`generation`). The URL goes to every `link[rel~="icon"]`.
 * - Nothing left: the page's own title and the original icon hrefs come back.
 *
 * Every write is made only on a difference, so the observer's own writes
 * settle in one pass instead of looping.
 */

const ORIG_HREF = 'data-lvdr-orig-href';
const ORIG_TYPE = 'data-lvdr-orig-type';

interface Entry {
  layer: HeadCueLayer;
  gate?: Promise<unknown> | undefined;
}

const layers = new Map<string, Entry>();
const subscribers = new Set<() => void>();
let renderer: FaviconRenderer | null = null;
let headObserver: MutationObserver | null = null;
/** The composed icon URL the links should carry; null = leave them alone. */
let composed: string | null = null;
/** Bumped by every change, so a render that finishes after a newer one is dropped. */
let generation = 0;
let renderQueued = false;
/** The prefixes last written into the title, so a removed layer's is still stripped. */
let written: string[] = [];

// `~=` matches the whole rel token: icon and "shortcut icon", never apple-touch-icon.
const iconLinks = () => document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');

/** document.title reads back with its whitespace stripped and collapsed. */
const asRead = (s: string) => s.replace(/\s+/g, ' ').trim();

function ordered(): HeadCueLayer[] {
  return [...layers.values()].map((e) => e.layer).sort((a, b) => a.order - b.order);
}

/**
 * The page's own title: strips, from the outside in, every prefix this
 * composer wrote or will write (and its trimEnd() form, which is how a prefix
 * reads back over an empty title), plus a stage prefix the pre-hydration hint
 * script painted before any layer existed.
 */
function restOf(title: string, prefixes: readonly string[]): string {
  const known = [...new Set(prefixes)].filter(Boolean);
  let rest = title;
  for (;;) {
    const before = rest;
    for (const p of known) {
      if (rest.startsWith(p)) rest = rest.slice(p.length);
      else if (rest === p.trimEnd()) rest = '';
    }
    rest = rest.replace(TITLE_PREFIX_RE, '');
    if (rest === before) return rest;
  }
}

function applyTitle(): void {
  const prefixes = ordered()
    .map((l) => l.titlePrefix ?? '')
    .filter(Boolean);
  const rest = restOf(document.title, [...prefixes, ...written]);
  const want = rest ? prefixes.join('') + rest : prefixes.join('').trimEnd();
  written = prefixes;
  if (document.title !== asRead(want)) document.title = want;
}

function applyIcons(): void {
  if (composed === null) return;
  for (const l of iconLinks()) {
    if (!l.hasAttribute(ORIG_HREF)) {
      l.setAttribute(ORIG_HREF, l.getAttribute('href') ?? '');
      const type = l.getAttribute('type');
      if (type !== null) l.setAttribute(ORIG_TYPE, type);
    }
    if (l.getAttribute('href') !== composed) l.setAttribute('href', composed);
    if (l.getAttribute('type') !== 'image/png') l.setAttribute('type', 'image/png');
  }
}

function restoreIcons(): void {
  composed = null;
  for (const l of iconLinks()) {
    if (!l.hasAttribute(ORIG_HREF)) continue;
    l.setAttribute('href', l.getAttribute(ORIG_HREF) ?? '');
    const type = l.getAttribute(ORIG_TYPE);
    if (type === null) l.removeAttribute('type');
    else l.setAttribute('type', type);
    l.removeAttribute(ORIG_HREF);
    l.removeAttribute(ORIG_TYPE);
  }
}

function onHead(): void {
  if (layers.size > 0) {
    applyTitle();
    applyIcons();
  }
  for (const fn of subscribers) fn();
  // What this pass wrote is not news: dropping it keeps a writer that slipped
  // its difference check from feeding itself.
  headObserver?.takeRecords();
}

function syncObserver(): void {
  if (typeof document === 'undefined') return;
  const wanted = layers.size > 0 || subscribers.size > 0;
  if (wanted && !headObserver) {
    headObserver = new MutationObserver(onHead);
    headObserver.observe(document.head, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['href', 'type', 'content'],
    });
  } else if (!wanted && headObserver) {
    headObserver.disconnect();
    headObserver = null;
  }
}

/** The ordered paints: by `order`, every `faviconLast` paint after the rest. */
function paints(): FaviconPaint[] {
  const ls = ordered();
  const first = ls.filter((l) => !l.faviconLast);
  const last = ls.filter((l) => l.faviconLast);
  return [...first, ...last].flatMap((l) => (l.favicon ? [l.favicon] : []));
}

async function render(): Promise<void> {
  renderQueued = false;
  const gen = generation;
  const ps = paints();
  if (ps.length === 0) {
    restoreIcons();
    return;
  }
  const gates = [...layers.values()].flatMap((e) => (e.gate ? [e.gate] : []));
  if (gates.length > 0) {
    try {
      await Promise.all(gates);
    } catch {
      return; // a failed gate holds the icon, as a never-opening one would
    }
    if (gen !== generation) return;
  }
  const url = await (renderer ?? composeFavicon)(ps).catch(() => null);
  if (gen !== generation || !url) return;
  composed = url;
  applyIcons();
}

function changed(): void {
  generation++;
  if (renderQueued) return;
  renderQueued = true;
  queueMicrotask(() => void render());
}

export const headCue: HeadCue = {
  setLayer(layer: HeadCueLayer, opts?: SetLayerOptions): void {
    layers.set(layer.id, { layer, gate: opts?.faviconGate });
    syncObserver();
    applyTitle();
    changed();
  },

  removeLayer(id: string): void {
    const had = layers.delete(id);
    syncObserver();
    // Runs even when the layer was absent: with none left it still strips a
    // stage prefix the pre-hydration hint painted; it writes only on a difference.
    applyTitle();
    if (layers.size === 0) {
      generation++;
      restoreIcons();
    } else if (had) {
      changed();
    }
  },

  setFaviconRenderer(r: FaviconRenderer | null): void {
    if (renderer === r) return;
    renderer = r;
    if (layers.size > 0) changed();
  },

  onHeadMutation(fn: () => void): () => void {
    subscribers.add(fn);
    syncObserver();
    return () => {
      subscribers.delete(fn);
      syncObserver();
    };
  },
};
